import { Assets, Container, Graphics, Sprite, Text, TextStyle, type Texture } from 'pixi.js';
import { FlipbookCharacter, buildFramePaths } from '../characters/FlipbookCharacter.js';
import type { Speaker } from '../story/caseModel.js';
import { Countdown } from './Countdown.js';
import { SpeechBubble } from './SpeechBubble.js';

/**
 * The staged Case 000 set: the approved lab artwork, Patch and Bit standing in
 * it, the countdown and the evidence readout. This is what turns the case from
 * a list of questions into a place.
 *
 * All artwork is the approved pilot artwork, re-encoded only
 * (docs/animation/VISUAL_ANIMATION_GUIDE.md) — nothing is repainted.
 */
const CHARACTER_ANIMATIONS = [
  { name: 'idle', frames: 24, fps: 12, loop: true },
  { name: 'alert', frames: 16, fps: 12, loop: true },
  { name: 'react-positive', frames: 18, fps: 12, loop: false },
  { name: 'react-negative', frames: 18, fps: 12, loop: false },
  { name: 'solved', frames: 16, fps: 12, loop: true },
  { name: 'partial', frames: 16, fps: 12, loop: true },
  { name: 'incorrect', frames: 16, fps: 12, loop: true },
] as const;

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
/** "The node will be disconnected in ten minutes" — the case's opening line. */
const COUNTDOWN_SECONDS = 600;
const SPEAKER_COLOR: Record<'patch' | 'bit', number> = { patch: 0x9fe8c0, bit: 0xffb366 };

async function loadCharacter(baseDir: string): Promise<FlipbookCharacter> {
  return FlipbookCharacter.load(
    CHARACTER_ANIMATIONS.map((animation) => ({
      name: animation.name,
      frameUrls: buildFramePaths(`${baseDir}/${animation.name}`, animation.frames, 3, 'webp'),
      fps: animation.fps,
      loop: animation.loop,
    })),
  );
}

interface CharacterSlot {
  readonly name: 'patch' | 'bit';
  readonly character: FlipbookCharacter;
  readonly baseScale: number;
  readonly baseX: number;
  readonly baseY: number;
  readonly parallax: number;
  /** Offsets each character's sway/talk-bob cycle so they don't move in lockstep. */
  readonly phaseOffset: number;
  popMs: number;
  /** Smoothed toward a target each frame by the speak()/clearSpeech() focus effect. */
  focusAlpha: number;
  focusScale: number;
}

export class SceneStage {
  readonly view = new Container();
  private background!: Sprite;
  private backgroundBaseX = 0;
  private backgroundBaseY = 0;
  private slots: CharacterSlot[] = [];
  /** The scene's ambient loop (idle/alert/an ending), restored once nobody's "speaking". */
  private currentMood = 'idle';
  private speakerFocus: Speaker = null;
  private bubble!: SpeechBubble;
  private countdown!: Countdown;
  private evidenceText!: Text;
  private readonly flash = new Graphics();
  private flashMs = 0;
  private flashColor = 0x9fe8c0;
  private elapsedMs = 0;
  private width = 0;
  private height = 0;

  static async create(width: number, height: number): Promise<SceneStage> {
    const stage = new SceneStage();
    await stage.build(width, height);
    return stage;
  }

  private async build(width: number, height: number): Promise<void> {
    this.width = width;
    this.height = height;

    const [backgroundTexture, patch, bit] = await Promise.all([
      Assets.load<Texture>('/scene/lab-background.webp'),
      loadCharacter('/characters/patch'),
      loadCharacter('/characters/bit'),
    ]);

    // Cover-fit the lab so the alert screens stay visible and the wet floor
    // lands at the bottom of the band, where the characters stand.
    this.background = new Sprite(backgroundTexture);
    this.background.anchor.set(0.5, 1);
    const cover =
      Math.max(width / backgroundTexture.width, height / backgroundTexture.height) * 1.08;
    this.background.scale.set(cover);
    this.backgroundBaseX = width / 2;
    this.backgroundBaseY = height;
    this.background.position.set(this.backgroundBaseX, this.backgroundBaseY);
    this.view.addChild(this.background);

    // Darken the lab slightly so the UI and characters read against it.
    this.view.addChild(
      new Graphics().rect(0, 0, width, height).fill({ color: 0x050a0b, alpha: 0.28 }),
    );

    // The blend into the console is drawn BEFORE the cast, so it darkens the
    // set without washing the characters out.
    this.buildGradient();

    const floorY = height * 0.97;
    this.slots = [
      this.placeCharacter(patch, 'patch', width * 0.32, floorY, Math.min(width * 0.42, 190), 1),
      this.placeCharacter(bit, 'bit', width * 0.75, floorY, Math.min(width * 0.32, 140), -0.6),
    ];

    this.buildHud();

    // Added last so it draws on top of the cast and the HUD.
    this.bubble = new SpeechBubble(width, Math.min(width * 0.74, 260));
    this.view.addChild(this.bubble.view);

    this.flash.rect(0, 0, width, height).fill({ color: 0xffffff });
    this.flash.alpha = 0;
    this.view.addChild(this.flash);
  }

  private placeCharacter(
    character: FlipbookCharacter,
    name: 'patch' | 'bit',
    x: number,
    feetY: number,
    targetWidth: number,
    parallax: number,
  ): CharacterSlot {
    const sprite = character.sprite;
    const baseScale = targetWidth / sprite.texture.width;
    sprite.scale.set(baseScale);
    sprite.position.set(x, feetY);
    this.view.addChild(sprite);
    return {
      name,
      character,
      baseScale,
      baseX: x,
      baseY: feetY,
      parallax,
      phaseOffset: name === 'patch' ? 0 : Math.PI * 0.6,
      popMs: 0,
      focusAlpha: 1,
      focusScale: 1,
    };
  }

  /** Fake vertical gradient blending the set into the console panel below. */
  private buildGradient(): void {
    const gradient = new Graphics();
    const bands = 14;
    const bandHeight = this.height * 0.3;
    for (let i = 0; i < bands; i++) {
      const alpha = (i / (bands - 1)) ** 2 * 0.95;
      gradient
        .rect(
          0,
          this.height - bandHeight + (i * bandHeight) / bands,
          this.width,
          bandHeight / bands + 1,
        )
        .fill({ color: 0x04080a, alpha });
    }
    this.view.addChild(gradient);
  }

  private buildHud(): void {
    this.countdown = new Countdown(COUNTDOWN_SECONDS, 1);
    this.countdown.view.position.set(this.width - 100, 26);
    this.view.addChild(this.countdown.view);

    const label = new Text({
      text: 'NODE OFFLINE IN',
      style: new TextStyle({ fill: 0xff6a5a, fontFamily: MONO, fontSize: 8, letterSpacing: 1 }),
    });
    label.anchor.set(1, 1);
    label.position.set(this.width - 12, 12);
    this.view.addChild(label);

    this.evidenceText = new Text({
      text: '',
      style: new TextStyle({ fill: 0x7fe0a8, fontFamily: MONO, fontSize: 11 }),
    });
    this.evidenceText.position.set(12, 14);
    this.view.addChild(this.evidenceText);
  }

  setEvidence(count: number, total: number): void {
    this.evidenceText.text = `EVIDENCE ${String(count).padStart(2, '0')}/${String(total)}`;
  }

  /** Sets both characters' resting mood (idle / alert / an ending loop), and
   * clears any speech — the base state speak() layers onto. */
  setMood(animation: string): void {
    this.currentMood = animation;
    this.speakerFocus = null;
    this.bubble.hide();
    for (const slot of this.slots) slot.character.play(animation);
  }

  /**
   * Patch or Bit poses `text` in a speech bubble anchored over their head:
   * they switch to the alert/worried loop, brighten and lean in, while the
   * other recedes slightly — this is how the case's questions reach the
   * player, instead of a narrator reciting them in the console. Resolves
   * once the line is fully typed (or immediately if `clearSpeech()`
   * interrupts it — e.g. the player already answered).
   */
  async speak(speaker: 'patch' | 'bit', text: string): Promise<void> {
    this.speakerFocus = speaker;
    for (const slot of this.slots) {
      slot.character.play(speaker === slot.name ? 'alert' : this.currentMood);
    }
    const slot = this.slots.find((candidate) => candidate.name === speaker);
    if (slot === undefined) return;
    const sprite = slot.character.sprite;
    const anchorX = sprite.x;
    const anchorY = sprite.y - sprite.height * 0.88; // roughly the hood/head, under the frame's margin
    await this.bubble.type(text, anchorX, anchorY, SPEAKER_COLOR[speaker]);
  }

  /** Dismisses the bubble and returns both characters to the ambient mood. */
  clearSpeech(): void {
    this.speakerFocus = null;
    this.bubble.hide();
    for (const slot of this.slots) slot.character.play(this.currentMood);
  }

  /** Plays a one-shot reaction that settles back into `mood`, with a foreground pop. */
  playReaction(reaction: string, mood: string): void {
    this.currentMood = mood;
    this.speakerFocus = null; // the player's own action takes over from whoever was "asking"
    this.bubble.hide();
    for (const slot of this.slots) {
      slot.character.play(reaction, mood);
      slot.popMs = 520;
    }
    this.flashColor = reaction === 'react-negative' ? 0xff4b3a : 0x7fffb0;
    this.flashMs = 420;
  }

  get countdownExpired(): boolean {
    return this.countdown.expired;
  }

  /** Restarts the ten-minute countdown, so a replay opens under fresh pressure. */
  resetCountdown(): void {
    this.countdown.reset(COUNTDOWN_SECONDS);
  }

  update(deltaMs: number): void {
    this.elapsedMs += deltaMs;
    this.countdown.update(deltaMs);
    this.bubble.update(deltaMs);

    // Slow breathing parallax so the set never feels like a static picture.
    const drift = Math.sin(this.elapsedMs / 5200) * 7;
    const rise = Math.sin(this.elapsedMs / 7100) * 4;
    this.background.position.set(this.backgroundBaseX + drift, this.backgroundBaseY + rise);

    for (const slot of this.slots) {
      const sprite = slot.character.sprite;
      sprite.x = slot.baseX - drift * 0.35 * slot.parallax;

      // Everyone gets a slow ambient sway so nobody ever looks frozen between
      // beats; whoever's actively "asking" gets a brisker, bigger bounce and
      // head-tilt layered on top — their energy while posing the question,
      // not just a held pose.
      const ambientPhase = this.elapsedMs / 900 + slot.phaseOffset;
      let bobY = Math.sin(ambientPhase) * 2.2;
      let tilt = Math.sin(ambientPhase * 0.8) * 0.02;

      const isActiveSpeaker = this.speakerFocus === slot.name;
      if (isActiveSpeaker) {
        const talkPhase = this.elapsedMs / 95 + slot.phaseOffset;
        bobY += Math.sin(talkPhase) * 6.5;
        tilt += Math.sin(talkPhase * 1.4 + 0.5) * 0.075;
      }
      sprite.y = slot.baseY + bobY;
      sprite.rotation = tilt;

      // Lean toward whoever's "asking" and dim the other one; neutral (both at
      // rest) once speakerFocus is null.
      const isSpeaker = this.speakerFocus === null || isActiveSpeaker;
      const targetAlpha = this.speakerFocus === null ? 1 : isSpeaker ? 1 : 0.7;
      const targetFocusScale = this.speakerFocus === null ? 1 : isSpeaker ? 1.08 : 0.95;
      const ease = Math.min(1, deltaMs / 180);
      slot.focusAlpha += (targetAlpha - slot.focusAlpha) * ease;
      slot.focusScale += (targetFocusScale - slot.focusScale) * ease;
      sprite.alpha = slot.focusAlpha;

      let pop = 0;
      if (slot.popMs > 0) {
        slot.popMs = Math.max(0, slot.popMs - deltaMs);
        const phase = 1 - slot.popMs / 520;
        pop = Math.sin(Math.PI * phase) * 0.09;
      }
      sprite.scale.set(slot.baseScale * (1 + pop) * slot.focusScale);
    }

    if (this.flashMs > 0) {
      this.flashMs = Math.max(0, this.flashMs - deltaMs);
      const phase = this.flashMs / 420;
      this.flash.tint = this.flashColor;
      this.flash.alpha = phase * phase * 0.42;
    }
  }

  destroy(): void {
    this.view.destroy({ children: true });
  }
}
